import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getEmployeeForEmployer } from "../../../slices/broker/thunk";
import Loading from "../../../Components/Loading";
import EmployeesTable from "../../../Components/Tables/EmployeesTable/EmployeesTable";
import EditEmployeeModal from "../../../Components/modals/EmployeesModal/EditEmployeeModal";
import AddEmployeesModal from "../../../Components/modals/EmployeesModal/AddEmployee/AddEmployeesModal";
import NavTabEmployee from "../../../Components/Tabs/NavTabEmployee";
import { getStatusEmployees } from "../../../utils/utils";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import { Container, Col } from "reactstrap";
import DropdownActionsTable from "../../../Components/Dropdown/DropdownActionsTable";
import { downloadEmployeesReportByUserId } from "../../../slices/general/thunk";
import { useCurrentUser } from "../../../slices/auth/hooks";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const DEFAULT_COUNTS = { active: 0, terminated: 0, pending: 0 };

function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const DasboardEmployeesEmployer = ({
  isLoaded,
  employeeId,
  openModal,
  setOpenModal,
}) => {
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const isNational = currentUser?.role === DASHBOARD_USER_ROLES.NATIONAL;
  const isAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState("1");
  const [isTerminated, setIsTerminated] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  const [countByStatus, setCountByStatus] = useState(DEFAULT_COUNTS);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [searchDone, setSearchDone] = useState(false);

  const toggleTab = (tab, status) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setIsTerminated(status === "terminated");
      setIsPending(status === "pending");
      setCurrentPage(0);
    }
  };

  const handleModalAddEmployee = () => setOpenModal(true);
  const handleChangePage = (page) => setCurrentPage(page);
  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0); // reset paging when searching
  };

  // request guard to avoid stale updates
  const fetchEmployees = useCallback(async () => {
    if (!isLoaded) return;
    const statusEmployees = getStatusEmployees(isTerminated, isPending);
    const reqKey = `${employeeId || id}-${statusEmployees}-${currentPage}-${debouncedSearchTerm}`;
    let cancelled = false;

    setLoading(true);
    setSearchDone(false);

    try {
      const response = await dispatch(
        getEmployeeForEmployer({
          id: employeeId || id,
          status: statusEmployees,
          page: currentPage,
          query: debouncedSearchTerm, // ✅ use debounced value
        })
      ).unwrap();

      if (cancelled) return;

      const counts = response?.countByStatus ?? DEFAULT_COUNTS;

      setCountByStatus({
        active: counts.active ?? 0,
        terminated: counts.terminated ?? 0,
        pending: counts.pending ?? 0,
      });

      setEmployees(response?.data ?? []);
      setTotalEmployees(response?.total ?? 0);
      setPageSize(response?.pageSize ?? 0);
      setTotalPages(response?.pages ?? 0);
      setSearchDone(true);
    } catch (err) {
      if (!cancelled) {
        console.error(err);
      }
    } finally {
      if (!cancelled) setLoading(false);
    }

    // cancel function bound to this call
    return () => {
      cancelled = true;
    };
  }, [
    isLoaded,
    dispatch,
    employeeId,
    id,
    isTerminated,
    isPending,
    currentPage,
    debouncedSearchTerm,
  ]);

  useEffect(() => {
    const cancel = fetchEmployees();
    return () => {
      // handle promise cancel guard
      if (typeof cancel === "function") cancel();
    };
  }, [fetchEmployees]);

  const hanldeRefreshTable = () => {
    fetchEmployees();
  };

  return (
    <React.Fragment>
      <EditEmployeeModal
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        editUser={selectedEmployee}
        setUser={setSelectedEmployee}
        onRefreshTable={hanldeRefreshTable}
      />
      {openModal && (
        <AddEmployeesModal
          open={openModal}
          setOpen={setOpenModal}
          onCreate={() => {
            // if we’re already on Pending, just refresh; otherwise switch
            if (activeTab === "2") hanldeRefreshTable();
            else toggleTab("2", "pending");
          }}
        />
      )}
      <Container fluid>
        <div className="col-12 d-flex justify-content-between mt-2 align-items-center">
          <div className="col-4 d-flex justify-content-end align-items-center">
            <SearchBar
              inputRef={inputRef}
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Filter by name or email"
            />
            {(isAdmin || isNational) && (
              <DropdownActionsTable
                downloadMethod={downloadEmployeesReportByUserId}
                params={{ dashboardUserId: employeeId || id }}
              />
            )}
          </div>
        </div>

        <Col xl={12}>
          <NavTabEmployee
            loading={loading}
            toggleTab={toggleTab}
            activeTab={activeTab}
            countByStatus={countByStatus} // ✅ always defined
          />

          <div className="table-container position-relative">
            {loading && <Loading />}
            <div className="live-preview">
              <div className="table-responsive">
                <EmployeesTable
                  activeTab={activeTab}
                  searchDone={searchDone}
                  searchParam={debouncedSearchTerm}
                  data={employees}
                  onRefreshTable={hanldeRefreshTable}
                  openModalAddEmployee={handleModalAddEmployee}
                  onEditClick={handleEditClick}
                  shouldShowImportMessage={true}
                  paginationProps={{
                    itemsCount: employees.length,
                    totalPages,
                    totalItems: totalEmployees,
                    currentPage,
                    onChangePage: handleChangePage,
                  }}
                  isTerminated={isTerminated}
                />
              </div>
            </div>
          </div>
        </Col>
      </Container>
      <div id="portal-root"></div>
    </React.Fragment>
  );
};

export default DasboardEmployeesEmployer;